import React, { useMemo } from 'react'
import styled from 'styled-components'
import { ModalProvider } from 'toonswap-uikit'
import { Web3ReactProvider, Web3ReactHooks } from '@web3-react/core'
import { Connector } from '@web3-react/types'
import { Provider } from 'react-redux'
// import { getLibrary } from 'utils/web3React'
import { LanguageProvider } from 'contexts/Localization'
import { ThemeContextProvider } from 'contexts/ThemeContext'
import { RefreshContextProvider } from 'contexts/RefreshContext'
import { TokenCreationContextProvider } from 'contexts/TokenCreationContext'
import store from 'state'
import useOrderedConnections from 'hooks/useOrderedConnections'
// import { NetworkContextName } from './constants'

/* Root popup theme: same as transaction/settings modal (dark header, light body).
 * When modal is open, first child has a sibling (overlay wrapper); its last child is modal content (StyledModal or our wrapper).
 * Support both: modal content = StyledModal (no wrapper) or wrapper > StyledModal (e.g. Settings/RecentTransactions). */
const RootModalTheme = styled.div`
  /* Overlay: full screen, no border radius (only the dialog content should be rounded) */
  [data-reach-dialog-overlay] {
    border-radius: 0 !important;
    position: fixed !important;
    top: 0 !important;
    left: 0 !important;
    right: 0 !important;
    bottom: 0 !important;
    width: 100% !important;
    height: 100% !important;
    max-width: none !important;
  }
  /* UIKit ModalWrapper (wallet/account modal backdrop): no border radius so it covers full screen */
  & > *:first-child:has(+ *) > *:last-child {
    border-radius: 0 !important;
  }
  /* Modal card only (inner content), not the overlay/wrapper - e.g. UIKit StyledModal inside ModalWrapper */
  & > *:first-child:has(+ *) > *:last-child > *:first-child {
    border-radius: 40px !important;
  }
  /* Your wallet / account modal: 86% width on mobile like other dialogs (inner card only) */
  @media (max-width: 768px) {
    & > *:first-child:has(+ *) > *:last-child > *:first-child {
      width: 86% !important;
      max-width: 86% !important;
    }
  }
  /* Header: when no wrapper (modal content is StyledModal) */
  & > *:first-child:has(+ *) > *:last-child > div:first-child {
    border-bottom: 1px solid #4a545d !important;
    padding: 14px 24px !important;
    color: #ffffff !important;
  }
  & > *:first-child:has(+ *) > *:last-child > div:first-child h2,
  & > *:first-child:has(+ *) > *:last-child > div:first-child button {
    color: #ffffff !important;
  }
  & > *:first-child:has(+ *) > *:last-child > div:first-child button svg,
  & > *:first-child:has(+ *) > *:last-child > div:first-child button path {
    fill: #ffffff !important;
    color: #ffffff !important;
  }
  /* Body: when no wrapper */
  & > *:first-child:has(+ *) > *:last-child > div:last-child {
    color: #e0e0e0;
  }
  & > *:first-child:has(+ *) > *:last-child > div:last-child a {
    color: #e0e0e0;
  }
  & > *:first-child:has(+ *) > *:last-child > div:last-child a:hover {
    color: #ffffff;
  }
  /* With wrapper: modal card is *:last-child > div:first-child; target its header and body */
  & > *:first-child:has(+ *) > *:last-child > div:first-child > div:first-child {
    border-bottom: 1px solid #4a545d !important;
    padding: 14px 24px !important;
    color: #ffffff !important;
  }
  & > *:first-child:has(+ *) > *:last-child > div:first-child > div:first-child h2,
  & > *:first-child:has(+ *) > *:last-child > div:first-child > div:first-child button {
    color: #ffffff !important;
  }
  & > *:first-child:has(+ *) > *:last-child > div:first-child > div:first-child button svg,
  & > *:first-child:has(+ *) > *:last-child > div:first-child > div:first-child button path {
    fill: #ffffff !important;
    color: #ffffff !important;
  }
  & > *:first-child:has(+ *) > *:last-child > div:first-child > div:last-child {
    color: #e0e0e0;
  }
  & > *:first-child:has(+ *) > *:last-child > div:first-child > div:last-child a {
    color: #e0e0e0;
  }
  & > *:first-child:has(+ *) > *:last-child > div:first-child > div:last-child a:hover {
    color: #ffffff;
  }
`

const Providers: React.FC = ({ children }) => {
  const connections = useOrderedConnections()

  const connectors: [Connector, Web3ReactHooks][] = connections.map(({ hooks, connector }) => [connector, hooks])

  const key = useMemo(() => connections.map((connection) => connection.name).join('-'), [connections])

  return (
    <Web3ReactProvider connectors={connectors} key={key}>
      <Provider store={store}>
        <ThemeContextProvider>
          <LanguageProvider>
            <RefreshContextProvider>
              <TokenCreationContextProvider>
                <RootModalTheme>
                  <ModalProvider>{children}</ModalProvider>
                </RootModalTheme>
              </TokenCreationContextProvider>
            </RefreshContextProvider>
          </LanguageProvider>
        </ThemeContextProvider>
      </Provider>
    </Web3ReactProvider>
  )
}

export default Providers
